import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useParams, useHistory } from "react-router-dom";
import SessionManager from "../Utils/SessionManager";
import MyLoader from "../Spinner/MyLoader";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";

// ============================================================================
// CONSTANTS
// ============================================================================

const API_ENDPOINTS = {
  GET_CLASSES: "getExamParticipatingClass.php",
  GET_FIELDS: "getExamSocialValueFieldMaster.php",
  GET_STUDENTS: "getClassStudentSocialValueMark.php",
  ADD_MARKS: "AddSocialValueFieldMarks.php",
  UPDATE_MAX_MARK: "UpdateSocialValueFieldMaxMark.php",
};

const buildApiUrl = (fn) =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${fn}`;

const createMarksKey = (studentId, fieldId) => `${studentId}_${fieldId}`;

const getFieldIdFromKey = (key) => {
  const parts = key.split("_");
  return parts.slice(1).join("_"); // Handle field_id that might contain underscores
};

const collectMarksForSave = (marksData, students, fields, maxMarks = {}) => {
  const marksArray = [];
  students.forEach((student) => {
    fields.forEach((field) => {
      const key = createMarksKey(student.student_enrollment_no, field.field_id);
      const data = marksData[key];
      if (data && (data.mark || data.grade)) {
        marksArray.push({
          student_id: student.student_enrollment_no,
          field_id: field.field_id,
          mark: data.mark || "",
          grade: data.grade || "",
          max_mark: maxMarks[field.field_id] || "100",
        });
      }
    });
  });
  return marksArray;
};

// ============================================================================
// API CALLS
// ============================================================================

const fetchParticipatingClasses = async (examId, sessionId) => {
  const schoolCode = SessionManager.getSchoolCode();
  const url = buildApiUrl(API_ENDPOINTS.GET_CLASSES);
  const params = { exam_id: examId, session_id: sessionId, school_code: schoolCode };
  try {
    const res = await axios.get(url, { params });
    if (res.data?.api_status === "OK") {
      return { success: true, data: res.data?.data || [] };
    }
    return { success: false, message: res.data?.api_remark || "Failed to load classes" };
  } catch {
    return { success: false, message: "Error loading classes" };
  }
};

const fetchSocialValueFields = async (examId, sessionId) => {
  const schoolCode = SessionManager.getSchoolCode();
  const url = buildApiUrl(API_ENDPOINTS.GET_FIELDS);
  const params = { exam_id: examId, session_id: sessionId, school_code: schoolCode };
  try {
    const res = await axios.get(url, { params });
    if (res.data?.api_status === "OK") {
      return { success: true, data: res.data?.data || [] };
    }
    return { success: false, message: res.data?.api_remark || "Failed to load fields" };
  } catch {
    return { success: false, message: "Error loading fields" };
  }
};

const fetchStudents = async (examId, sessionId, classId, sectionId) => {
  const schoolCode = SessionManager.getSchoolCode();
  const url = buildApiUrl(API_ENDPOINTS.GET_STUDENTS);
  const params = {
    exam_id: String(examId),
    session_id: String(sessionId),
    class_id: String(classId),
    section_id: String(sectionId || "No"),
    school_code: String(schoolCode),
  };
  try {
    const res = await axios.get(url, { params });
    if (res.data?.api_status === "OK") {
      return { success: true, data: res.data?.data || [] };
    }
    return { success: false, message: res.data?.api_remark || "Failed to load students" };
  } catch {
    return { success: false, message: "Error loading students" };
  }
};

const updateMaxMark = async (examId, sessionId, classId, sectionId, fieldId, maxMark) => {
  const schoolCode = SessionManager.getSchoolCode();
  const url = buildApiUrl(API_ENDPOINTS.UPDATE_MAX_MARK);
  
  // Use POST for consistency
  const formData = new URLSearchParams();
  formData.append('exam_id', String(examId));
  formData.append('session_id', String(sessionId));
  formData.append('class_id', String(classId));
  formData.append('section_id', String(sectionId || "No"));
  formData.append('school_code', String(schoolCode));
  formData.append('field_id', String(fieldId));
  formData.append('max_mark', String(maxMark));

  try {
    const response = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    if (response.data?.api_status === "OK") {
      return { success: true, message: "Max mark updated successfully" };
    }
    return {
      success: false,
      message: response.data?.api_remark || "Failed to update max mark",
    };
  } catch (error) {
    console.error("Error updating max mark:", error);
    return {
      success: false,
      message: "Error updating max mark",
    };
  }
};

const saveMarksData = async (examId, sessionId, classId, sectionId, marksData) => {
  const schoolCode = SessionManager.getSchoolCode();
  const url = buildApiUrl(API_ENDPOINTS.ADD_MARKS);
  const formData = new URLSearchParams();
  formData.append("exam_id", String(examId));
  formData.append("session_id", String(sessionId));
  formData.append("class_id", String(classId));
  formData.append("section_id", String(sectionId || "No"));
  formData.append("school_code", String(schoolCode));
  formData.append("marks_data", JSON.stringify(marksData));
  try {
    const res = await axios.post(url, formData, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    if (res.data?.api_status === "OK") {
      return { success: true, message: "Marks saved successfully" };
    }
    return { success: false, message: res.data?.api_remark || "Failed to save marks" };
  } catch (e) {
    return { success: false, message: "Error saving marks" };
  }
};

// ============================================================================
// SUBCOMPONENTS (compact versions)
// ============================================================================

const ClassSelector = ({ classList, selectedIndex, onSelect }) => {
  if (classList.length === 0) return null;

  const primaryColor = COLORS.primary_color_shade5 || "#3b82f6";
  const primaryColorDark = COLORS.primary_color_shade4 || "#2563eb";

  return (
    <div style={{ marginBottom: "12px" }}>
      <div
        style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColorDark} 100%)`,
          borderRadius: "12px",
          padding: "12px",
          boxShadow: `0 8px 24px ${primaryColor}30`,
          marginBottom: "6px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            justifyContent: "flex-start",
          }}
        >
          {classList.map((item, index) => {
            const isSelected = selectedIndex === index;
            return (
              <div
                key={index}
                onClick={() => onSelect(index)}
                style={{
                  cursor: "pointer",
                  padding: "8px 16px",
                  borderRadius: "10px",
                  background: isSelected
                    ? "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)"
                    : "rgba(255, 255, 255, 0.15)",
                  border: isSelected
                    ? "2px solid #ffffff"
                    : "2px solid rgba(255, 255, 255, 0.3)",
                  boxShadow: isSelected
                    ? `0 6px 16px rgba(0, 0, 0, 0.12), 0 0 0 3px ${primaryColor}30`
                    : "0 3px 8px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  transform: isSelected ? "translateY(-2px) scale(1.02)" : "translateY(0) scale(1)",
                  position: "relative",
                  overflow: "hidden",
                  minWidth: "110px",
                  textAlign: "center",
                  animation: `fadeInUp 0.4s ease-out ${index * 0.05}s both`,
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.25)";
                    e.currentTarget.style.transform = "translateY(-2px) scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)";
                    e.currentTarget.style.transform = "translateY(0) scale(1)";
                    e.currentTarget.style.boxShadow = "0 3px 8px rgba(0, 0, 0, 0.08)";
                  }
                }}
              >
                {isSelected && (
                  <div
                    style={{
                      position: "absolute",
                      top: "4px",
                      right: "4px",
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      boxShadow: "0 2px 4px rgba(16, 185, 129, 0.4)",
                      animation: "scaleIn 0.3s ease-out",
                    }}
                  />
                )}
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    fontWeight: isSelected ? "600" : "500",
                    color: isSelected ? primaryColor : "#ffffff",
                    letterSpacing: "0.3px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.section_id === "No"
                    ? item.class_name
                    : `${item.class_name}:${item.section_name}`}
                </p>
              </div>
            );
          })}
          </div>
        </div>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .no-spinner::-webkit-inner-spin-button,
        .no-spinner::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .no-spinner {
          -moz-appearance: textfield;
        }
      `}</style>
      </div>
  );
};

const FieldSelector = ({ fields, selectedFields, onToggleField, students, maxMarks, onMaxMarkChange }) => {
  if (!fields || fields.length === 0) return null;

  // Get max_mark for each field from student marks or state
  const getMaxMarkForField = (fieldId) => {
    if (maxMarks && maxMarks[fieldId] !== undefined) {
      return maxMarks[fieldId];
    }
    if (!students || students.length === 0) return "100";
    for (const student of students) {
      const fieldMark = student.social_value_marks?.find((mark) => mark.field_id === fieldId);
      if (fieldMark && fieldMark.max_mark) {
        return fieldMark.max_mark;
      }
    }
    return "100"; // Default max mark
  };

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: "16px",
        border: "2px solid #e5e7eb",
        padding: "10px 14px",
        marginBottom: "16px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)",
        transition: "all 0.3s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "6px",
        }}
      >
        <i className="fa fa-check-square" style={{ fontSize: "14px", color: COLORS.primary_color_shade5 || "#3b82f6" }}></i>
        <h4
          style={{
            margin: 0,
            fontSize: "13px",
            fontWeight: "600",
            color: "#111827",
          }}
        >
          Select Fields to Display
        </h4>
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        {fields.map((field) => {
          const isSelected = selectedFields.includes(field.field_id);
          const primary = COLORS.primary_color_shade5 || "#3b82f6";
          return (
            <label
              key={field.field_id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "5px 10px",
                borderRadius: "10px",
                background: isSelected
                  ? `linear-gradient(135deg, ${primary}20 0%, ${primary}10 100%)`
                  : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                border: isSelected
                  ? `2px solid ${primary}`
                  : "2px solid #e5e7eb",
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                fontSize: "12px",
                fontWeight: isSelected ? "700" : "600",
                color: isSelected ? primary : "#64748b",
                boxShadow: isSelected
                  ? `0 4px 12px ${primary}25, 0 2px 4px rgba(0, 0, 0, 0.1)`
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transform: isSelected ? "translateY(-2px)" : "translateY(0)",
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.background = "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)";
                  e.currentTarget.style.borderColor = "#cbd5e1";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)";
                } else {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = `0 6px 16px ${primary}30, 0 4px 8px rgba(0, 0, 0, 0.12)`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.background = "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.05)";
                } else {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = `0 4px 12px ${primary}25, 0 2px 4px rgba(0, 0, 0, 0.1)`;
                }
              }}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleField(field.field_id)}
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                  accentColor: primary,
                }}
              />
              <span>{field.field_name}</span>
              <span
                style={{
                  fontSize: "11px",
                  color: "#94a3b8",
                  marginLeft: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                (Max:
                <input
                  type="number"
                  value={getMaxMarkForField(field.field_id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    // Update local state immediately for UI
                    if (onMaxMarkChange) {
                      onMaxMarkChange(field.field_id, e.target.value, false);
                    }
                  }}
                  onBlur={(e) => {
                    e.stopPropagation();
                    // Update styling
                    e.target.style.borderColor = "#cbd5e1";
                    e.target.style.boxShadow = "none";
                    e.target.style.transform = "scale(1)";
                    // Save to database on blur
                    if (onMaxMarkChange) {
                      onMaxMarkChange(field.field_id, e.target.value, true);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    width: "70px",
                    padding: "3px 5px",
                    fontSize: "12px",
                    border: "2px solid #cbd5e1",
                    borderRadius: "8px",
                    textAlign: "center",
                    background: "#ffffff",
                    color: "#475569",
                    fontWeight: "600",
                    transition: "all 0.2s ease",
                    MozAppearance: "textfield",
                  }}
                  className="no-spinner"
                  onFocus={(e) => {
                    e.target.style.borderColor = COLORS.primary_color_shade5 || "#3b82f6";
                    e.target.style.boxShadow = `0 0 0 3px ${(COLORS.primary_color_shade5 || "#3b82f6")}20`;
                    e.target.style.transform = "scale(1.05)";
                  }}
                  min="1"
                  max="999"
                />
                )
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

const MarkGradeControl = ({ showMark, showGrade, onToggleMark, onToggleGrade }) => {
  return (
    <div
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: "12px",
        border: "2px solid #e5e7eb",
        padding: "8px 12px",
        marginBottom: "12px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
        display: "flex",
        alignItems: "center",
        gap: "16px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <i className="fa fa-check-circle" style={{ fontSize: "12px", color: COLORS.primary_color_shade5 || "#3b82f6" }}></i>
        <span style={{ fontSize: "12px", fontWeight: "600", color: "#111827" }}>Display:</span>
      </div>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "4px 10px",
          borderRadius: "8px",
          background: showMark
            ? `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"}20 0%, ${COLORS.primary_color_shade5 || "#3b82f6"}10 100%)`
            : "transparent",
          border: showMark
            ? `2px solid ${COLORS.primary_color_shade5 || "#3b82f6"}`
            : "2px solid #e5e7eb",
          cursor: "pointer",
          transition: "all 0.2s ease",
          fontSize: "12px",
          fontWeight: showMark ? "700" : "600",
          color: showMark ? COLORS.primary_color_shade5 || "#3b82f6" : "#64748b",
        }}
        onMouseEnter={(e) => {
          if (!showMark) {
            e.currentTarget.style.background = "#f1f5f9";
            e.currentTarget.style.borderColor = "#cbd5e1";
          }
        }}
        onMouseLeave={(e) => {
          if (!showMark) {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "#e5e7eb";
          }
        }}
      >
        <input
          type="checkbox"
          checked={showMark}
          onChange={onToggleMark}
          style={{
            width: "14px",
            height: "14px",
            cursor: "pointer",
            accentColor: COLORS.primary_color_shade5 || "#3b82f6",
          }}
        />
        <span>Obtained Mark</span>
      </label>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "4px 10px",
          borderRadius: "8px",
          background: showGrade
            ? `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"}20 0%, ${COLORS.primary_color_shade5 || "#3b82f6"}10 100%)`
            : "transparent",
          border: showGrade
            ? `2px solid ${COLORS.primary_color_shade5 || "#3b82f6"}`
            : "2px solid #e5e7eb",
          cursor: "pointer",
          transition: "all 0.2s ease",
          fontSize: "12px",
          fontWeight: showGrade ? "700" : "600",
          color: showGrade ? COLORS.primary_color_shade5 || "#3b82f6" : "#64748b",
        }}
        onMouseEnter={(e) => {
          if (!showGrade) {
            e.currentTarget.style.background = "#f1f5f9";
            e.currentTarget.style.borderColor = "#cbd5e1";
          }
        }}
        onMouseLeave={(e) => {
          if (!showGrade) {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "#e5e7eb";
          }
        }}
      >
        <input
          type="checkbox"
          checked={showGrade}
          onChange={onToggleGrade}
          style={{
            width: "14px",
            height: "14px",
            cursor: "pointer",
            accentColor: COLORS.primary_color_shade5 || "#3b82f6",
          }}
        />
        <span>Obtained Grade</span>
      </label>
    </div>
  );
};

const ActionButtons = ({ onSave, onCancel, saving }) => (
  <div style={{ 
    display: "flex", 
    gap: "12px", 
    marginTop: "20px",
    padding: "16px",
    background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
    borderRadius: "12px",
    border: "2px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  }}>
    <button
      onClick={onSave}
      disabled={saving}
      style={{
        padding: "12px 24px",
        fontSize: "14px",
        fontWeight: "700",
        borderRadius: "12px",
        background: saving
          ? "#9ca3af"
          : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
        color: "#fff",
        border: "none",
        boxShadow: saving ? "none" : "0 4px 12px rgba(16, 185, 129, 0.35)",
        minWidth: "140px",
        cursor: saving ? "not-allowed" : "pointer",
        transition: "all 0.3s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
      }}
      onMouseEnter={(e) => {
        if (!saving) {
          e.target.style.transform = "translateY(-3px) scale(1.02)";
          e.target.style.boxShadow = "0 8px 20px rgba(16, 185, 129, 0.45)";
        }
      }}
      onMouseLeave={(e) => {
        if (!saving) {
          e.target.style.transform = "translateY(0) scale(1)";
          e.target.style.boxShadow = "0 4px 12px rgba(16, 185, 129, 0.35)";
        }
      }}
    >
      {saving ? (
        <>
          <i className="fa fa-spinner fa-spin"></i>
          Saving...
        </>
      ) : (
        <>
          <i className="fa fa-check"></i>
          Save All Marks
        </>
      )}
    </button>
    <button
      onClick={onCancel}
      style={{
        padding: "12px 24px",
        fontSize: "14px",
        fontWeight: "700",
        borderRadius: "12px",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        color: "#6b7280",
        border: "2px solid #e5e7eb",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
        minWidth: "120px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
      }}
      onMouseEnter={(e) => {
        e.target.style.background = "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)";
        e.target.style.borderColor = "#cbd5e1";
        e.target.style.transform = "translateY(-2px)";
        e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)";
      }}
      onMouseLeave={(e) => {
        e.target.style.background = "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)";
        e.target.style.borderColor = "#e5e7eb";
        e.target.style.transform = "translateY(0)";
        e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.05)";
      }}
    >
      <i className="fa fa-times"></i>
      Cancel
    </button>
  </div>
);

const MarksTable = ({ students, fields, marksData, onMarksChange, onSaveStudent, savingMap, showObtainedMark = true, showObtainedGrade = true }) => {
  const primary = COLORS.primary_color_shade5 || "#3b82f6";
  if (!fields?.length) return null;
  return (
    <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb" }}>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            <th style={{ background: primary, color: "#fff", padding: "10px 12px", textAlign: "left" }}>Roll No</th>
            <th style={{ background: primary, color: "#fff", padding: "10px 12px", textAlign: "left" }}>Student Name</th>
            {fields.map((f) => (
              <th key={f.field_id} style={{ background: primary, color: "#fff", padding: "10px 12px", textAlign: "center" }}>
                {f.field_name}
              </th>
            ))}
            <th style={{ background: primary, color: "#fff", padding: "10px 12px", textAlign: "center" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {students.map((stu, idx) => (
            <tr key={stu.student_enrollment_no} style={{ background: idx % 2 ? "#fafafa" : "#fff", borderBottom: "1px solid #f1f5f9" }}>
              <td style={{ padding: "10px 12px" }}>{stu.roll_no || stu.rollno || "-"}</td>
              <td style={{ padding: "10px 12px" }}>{stu.stu_name || stu.student_name || "-"}</td>
              {fields.map((f) => {
                const key = createMarksKey(stu.student_enrollment_no, f.field_id);
                const value = marksData[key] || { mark: "", grade: "" };
                return (
                  <td key={f.field_id} style={{ padding: "10px 12px", textAlign: "center" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
                      {showObtainedMark && (
                        <input
                          type="number"
                          value={value.mark ?? ""}
                          onChange={(e) => onMarksChange(key, { ...value, mark: e.target.value })}
                          placeholder="Mark"
                          className="no-spinner"
                          min="0"
                          max="999"
                          style={{ width: 70, textAlign: "center", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 8px" }}
                        />
                      )}
                      {showObtainedGrade && (
                        <input
                          type="text"
                          value={value.grade ?? ""}
                          onChange={(e) => onMarksChange(key, { ...value, grade: e.target.value.toUpperCase() })}
                          placeholder="Grade"
                          style={{ width: 70, textAlign: "center", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 8px" }}
                        />
                      )}
                    </div>
                  </td>
                );
              })}
              <td style={{ padding: "10px 12px", textAlign: "center" }}>
                <button
                  onClick={() => onSaveStudent(stu)}
                  disabled={!!savingMap[stu.student_enrollment_no]}
                  style={{
                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "6px 10px",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {savingMap[stu.student_enrollment_no] ? "Saving..." : "Save"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// MAIN
// ============================================================================

const AddExamSocialValueField = () => {
  const { exam_id, session_id, exam_name } = useParams();
  const history = useHistory();

  const EXAM_ID = exam_id || "";
  const SESSION_ID = session_id || "";
  const EXAM_NAME = decodeURIComponent(exam_name || "");

  const [loading, setLoading] = useState(false);
  const [classList, setClassList] = useState([]);
  const [selectedClassIndex, setSelectedClassIndex] = useState(-1);
  const [fields, setFields] = useState([]);
  const [students, setStudents] = useState([]);
  const [marksData, setMarksData] = useState({});
  const [savingMap, setSavingMap] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedFieldIds, setSelectedFieldIds] = useState([]);
  const [showObtainedMark, setShowObtainedMark] = useState(true);
  const [showObtainedGrade, setShowObtainedGrade] = useState(true);
  const [maxMarks, setMaxMarks] = useState({});

  const selectedClass = useMemo(() => {
    return selectedClassIndex >= 0 ? classList[selectedClassIndex] : null;
  }, [selectedClassIndex, classList]);

  const initializeMarks = useCallback((stuList, fieldList) => {
    const result = {};
    stuList.forEach((s) => {
      fieldList.forEach((f) => {
        const key = createMarksKey(s.student_enrollment_no, f.field_id);
        const existing = s.social_value_marks?.find((m) => m.field_id === f.field_id);
        result[key] = { mark: existing?.obt_mark || "", grade: existing?.grade || "" };
      });
    });
    return result;
  }, []);

  const fetchInitial = useCallback(async () => {
    if (!EXAM_ID || !SESSION_ID) return;
    setLoading(true);
    try {
      const [cls, flds] = await Promise.all([
        fetchParticipatingClasses(EXAM_ID, SESSION_ID),
        fetchSocialValueFields(EXAM_ID, SESSION_ID),
      ]);
      if (cls.success) setClassList(cls.data || []);
      else myToast.notify_warning(cls.message);

      if (flds.success) {
        const list = flds.data || [];
        setFields(list);
        // default single field selection
        if (list.length > 0) {
          setSelectedFieldIds([list[0].field_id]);
        } else {
          setSelectedFieldIds([]);
        }
      }
      else myToast.notify_warning(flds.message);
    } catch (e) {
      myToast.notify_error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [EXAM_ID, SESSION_ID]);

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  const fetchStudentsOfClass = useCallback(
    async (classId, sectionId) => {
      if (!classId) return;
      setLoadingStudents(true);
      setStudents([]);
      setMarksData({});
      try {
        const res = await fetchStudents(EXAM_ID, SESSION_ID, classId, sectionId);
        if (res.success) {
          const studentData = res.data || [];
          setStudents(studentData);
          setMarksData(initializeMarks(studentData, fields));
          
          // Initialize max marks from student data
          const initialMaxMarks = {};
          if (studentData.length > 0 && fields.length > 0) {
            fields.forEach((field) => {
              const firstStudent = studentData[0];
              const fieldMark = firstStudent.social_value_marks?.find(
                (mark) => mark.field_id === field.field_id
              );
              initialMaxMarks[field.field_id] = fieldMark?.max_mark || "100";
            });
          } else {
            // Default to 100 for all fields if no students
            fields.forEach((field) => {
              initialMaxMarks[field.field_id] = "100";
            });
          }
          setMaxMarks(initialMaxMarks);
        } else {
          myToast.notify_warning(res.message);
          setStudents([]);
          setMarksData({});
        }
      } catch {
        myToast.notify_error("Failed to load students");
        setStudents([]);
        setMarksData({});
      } finally {
        setLoadingStudents(false);
      }
    },
    [EXAM_ID, SESSION_ID, fields, initializeMarks]
  );

  useEffect(() => {
    if (selectedClass?.class_id) {
      fetchStudentsOfClass(selectedClass.class_id, selectedClass.section_id);
    } else {
      setStudents([]);
      setMarksData({});
    }
  }, [selectedClass, fetchStudentsOfClass]);

  const handleToggleField = useCallback((fieldId) => {
    // Allow multi-select; ensure at least one remains selected
    setSelectedFieldIds((prev) => {
      const exists = prev.includes(fieldId);
      if (exists) {
        if (prev.length === 1) return prev; // keep at least one
        return prev.filter((id) => id !== fieldId);
      }
      return [...prev, fieldId];
    });
  }, []);

  // Initialize max marks when fields or students change
  useEffect(() => {
    if (fields.length > 0 && students.length > 0) {
      if (Object.keys(maxMarks).length === 0) {
        const initialMaxMarks = {};
        fields.forEach((field) => {
          const firstStudent = students[0];
          const fieldMark = firstStudent.social_value_marks?.find(
            (mark) => mark.field_id === field.field_id
          );
          initialMaxMarks[field.field_id] = fieldMark?.max_mark || "100";
        });
        setMaxMarks(initialMaxMarks);
      }
    }
  }, [fields, students, maxMarks]);

  const handleMarksChange = useCallback((key, value) => {
    // Sanitize mark input: ensure 0 <= mark <= max_mark for the field
    let next = value;
    if (value && typeof value.mark !== "undefined") {
      const fieldId = getFieldIdFromKey(key);
      const max = parseFloat(maxMarks?.[fieldId] ?? "100");
      const raw = value.mark;
      if (raw === "" || raw === null) {
        // allow empty (treated as not entered)
      } else {
        let num = Number(raw);
        if (!Number.isNaN(num)) {
          if (num < 0) num = 0;
          if (num > max) num = max;
          next = { ...value, mark: String(num) };
        }
      }
    }
    setMarksData((prev) => ({
      ...prev,
      [key]: next,
    }));
  }, [maxMarks]);

  const handleMaxMarkChange = useCallback(async (fieldId, value, saveToDatabase = false) => {
    const maxMarkValue = value || "100";
    
    // Update local state immediately for UI responsiveness
    setMaxMarks((prev) => ({
      ...prev,
      [fieldId]: maxMarkValue,
    }));

    // Save to database only if saveToDatabase is true (on blur) and class is selected
    if (saveToDatabase && selectedClassIndex >= 0 && classList[selectedClassIndex]) {
      const selectedClass = classList[selectedClassIndex];
      
      try {
        const result = await updateMaxMark(
          EXAM_ID,
          SESSION_ID,
          selectedClass.class_id,
          selectedClass.section_id,
          fieldId,
          maxMarkValue
        );
        
        if (result.success) {
          myToast.notify_success(`Max mark updated to ${maxMarkValue}`);
          // Reload students to reflect the change
          await fetchStudentsOfClass(selectedClass.class_id, selectedClass.section_id);
        } else {
          // Revert on error
          setMaxMarks((prev) => ({
            ...prev,
            [fieldId]: prev[fieldId] || "100",
          }));
          myToast.notify_error(result.message);
        }
      } catch (error) {
        console.error("Error updating max mark:", error);
        // Revert on error
        setMaxMarks((prev) => ({
          ...prev,
          [fieldId]: prev[fieldId] || "100",
        }));
        myToast.notify_error("Failed to update max mark");
      }
    }
  }, [selectedClassIndex, classList, EXAM_ID, SESSION_ID, fetchStudentsOfClass]);

  const handleToggleMark = useCallback(() => {
    setShowObtainedMark((prev) => {
      const newValue = !prev;
      // Ensure at least one is enabled
      if (!newValue && !showObtainedGrade) {
        myToast.notify_warning("At least one field (Mark or Grade) must be enabled");
        return prev; // Don't change if both would be disabled
      }
      return newValue;
    });
  }, [showObtainedGrade]);

  const handleToggleGrade = useCallback(() => {
    setShowObtainedGrade((prev) => {
      const newValue = !prev;
      // Ensure at least one is enabled
      if (!newValue && !showObtainedMark) {
        myToast.notify_warning("At least one field (Mark or Grade) must be enabled");
        return prev; // Don't change if both would be disabled
      }
      return newValue;
    });
  }, [showObtainedMark]);

  const handleSaveStudent = useCallback(
    async (student) => {
      if (!selectedClass) {
        myToast.notify_warning("Please select a class");
        return;
      }
      // Only save marks for selected fields
      const fieldsToSave = fields.filter((field) => selectedFieldIds.includes(field.field_id));
      
      // Validate all entered marks for this student
      for (const field of fieldsToSave) {
        const key = createMarksKey(student.student_enrollment_no, field.field_id);
        const data = marksData[key];
        if (data && data.mark !== "" && data.mark !== null && typeof data.mark !== "undefined") {
          const markNum = Number(data.mark);
          const max = Number(maxMarks?.[field.field_id] ?? 100);
          if (Number.isNaN(markNum) || markNum < 0 || markNum > max) {
            myToast.notify_warning(`Invalid mark for ${student.stu_name || student.student_name} in ${field.field_name}. Enter 0 - ${max}`);
            return;
          }
        }
      }
      
      const studentMarks = collectMarksForSave(marksData, [student], fieldsToSave, maxMarks);
      
      if (studentMarks.length === 0) {
        myToast.notify_warning("Enter at least one mark or grade");
        return;
      }
      setSavingMap((p) => ({ ...p, [student.student_enrollment_no]: true }));
      try {
        const result = await saveMarksData(
          EXAM_ID,
          SESSION_ID,
          selectedClass.class_id,
          selectedClass.section_id,
          studentMarks
        );
        if (result.success) {
          myToast.notify_success(`Saved for ${student.stu_name || student.student_name}`);
        } else {
          myToast.notify_error(result.message);
        }
      } finally {
        setSavingMap((p) => {
          const cp = { ...p };
          delete cp[student.student_enrollment_no];
          return cp;
        });
      }
    },
    [EXAM_ID, SESSION_ID, selectedClass, fields, marksData, selectedFieldIds, maxMarks]
  );

  const handleSaveAll = useCallback(async () => {
    if (!selectedClass) {
      myToast.notify_warning("Please select a class");
      return;
    }
    if (selectedFieldIds.length === 0) {
      myToast.notify_warning("Please select at least one field");
      return;
    }
    // Only save marks for selected fields
    const fieldsToSave = fields.filter((field) => selectedFieldIds.includes(field.field_id));
    
    // Validate all entered marks for all students
    for (const student of students) {
      for (const field of fieldsToSave) {
        const key = createMarksKey(student.student_enrollment_no, field.field_id);
        const data = marksData[key];
        if (data && data.mark !== "" && data.mark !== null && typeof data.mark !== "undefined") {
          const markNum = Number(data.mark);
          const max = Number(maxMarks?.[field.field_id] ?? 100);
          if (Number.isNaN(markNum) || markNum < 0 || markNum > max) {
            myToast.notify_warning(`Invalid mark for ${student.stu_name || student.student_name} in ${field.field_name}. Enter 0 - ${max}`);
            return;
          }
        }
      }
    }
    
    const marksToSave = collectMarksForSave(marksData, students, fieldsToSave, maxMarks);
    
    if (marksToSave.length === 0) {
      myToast.notify_warning("Enter at least one mark or grade");
      return;
    }
    setSavingAll(true);
    try {
        const result = await saveMarksData(
          EXAM_ID,
          SESSION_ID,
          selectedClass.class_id,
          selectedClass.section_id,
          marksToSave
        );
      if (result.success) {
        myToast.notify_success("All saved");
        // reload to reflect
        await fetchStudentsOfClass(selectedClass.class_id, selectedClass.section_id);
      } else {
        myToast.notify_error(result.message);
      }
    } finally {
      setSavingAll(false);
    }
  }, [EXAM_ID, SESSION_ID, selectedClass, students, fields, marksData, selectedFieldIds, maxMarks, fetchStudentsOfClass]);

  const handleCancel = useCallback(() => {
    history.push(
      `/AdminMain/ManageSocialValueField/${EXAM_ID}/${SESSION_ID}/${encodeURIComponent(EXAM_NAME)}/Active`
    );
  }, [history, EXAM_ID, SESSION_ID, EXAM_NAME]);

  if (loading) {
    return <MyLoader />;
  }

  return (
    <div className="content-wrapper">
      <div className="content">
        <div className="container-fluid">
          <div className="content-header">
            <div
              className="leftside-content-header"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}
            >
              <ul className="breadcrumbs">
                <li>
                  <i className="fa fa-home" aria-hidden="true" />
                  <a href="#">Add Social Value Mark/Grade</a>
                </li>
              </ul>
              <BackButton
                onClick={() => history.goBack()}
                style={{ height: 28, fontSize: 11, padding: "2px 6px", marginTop: 0 }}
              />
            </div>
          </div>

          <div
            className="bg_page_white_rounded box_shadow"
            style={{
              margin: "10px 10px",
              padding: 0,
              borderRadius: 16,
              overflow: "hidden",
              background: "#ffffff",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            <div
              style={{
                background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade5 || "#2563eb"} 100%)`,
                padding: "10px 16px",
                color: "#ffffff",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#fff" }}>
                    Add Social Value Mark/Grade
                  </h2>
                  <p style={{ margin: "2px 0 0 0", fontSize: 11, color: "rgba(255,255,255,.9)" }}>
                    <i className="fa fa-graduation-cap" style={{ marginRight: 4, fontSize: 10 }}></i>
                    Exam: {EXAM_NAME}
                  </p>
                </div>
              </div>
            </div>

            <div style={{ padding: "12px 16px" }}>
              <ClassSelector
                classList={classList}
                selectedIndex={selectedClassIndex}
                onSelect={setSelectedClassIndex}
              />

              {selectedClass && (
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: 12,
                    border: "1px solid #e5e7eb",
                    overflow: "hidden",
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 16px",
                      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                      borderBottom: "2px solid #e5e7eb",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 8,
                          background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade5 || "#2563eb"} 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: 16,
                        }}
                      >
                        <i className="fa fa-table"></i>
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>
                          Marks Entry
                        </h3>
                        <p style={{ margin: "2px 0 0 0", fontSize: 11, color: "#6b7280" }}>
                          {students.length} student{students.length !== 1 ? "s" : ""} • {fields.length} field
                          {fields.length !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#111827" }}>
                        <input
                          type="checkbox"
                          checked={showObtainedMark}
                          onChange={(e) => setShowObtainedMark(e.target.checked)}
                          style={{ cursor: "pointer" }}
                        />
                        Obtained Mark
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#111827" }}>
                        <input
                          type="checkbox"
                          checked={showObtainedGrade}
                          onChange={(e) => setShowObtainedGrade(e.target.checked)}
                          style={{ cursor: "pointer" }}
                        />
                        Obtained Grade
                      </label>
                      <button
                        onClick={handleSaveAll}
                        disabled={savingAll}
                        style={{
                          background: savingAll ? "#9ca3af" : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 12,
                          padding: "10px 16px",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: savingAll ? "not-allowed" : "pointer",
                        }}
                      >
                        {savingAll ? "Saving..." : "Save All"}
                      </button>
                    </div>
                  </div>

                  <div style={{ padding: 12 }}>
                    <MarkGradeControl
                      showMark={showObtainedMark}
                      showGrade={showObtainedGrade}
                      onToggleMark={handleToggleMark}
                      onToggleGrade={handleToggleGrade}
                    />
                    <FieldSelector 
                      fields={fields} 
                      selectedFields={selectedFieldIds} 
                      onToggleField={handleToggleField}
                      students={students}
                      maxMarks={maxMarks}
                      onMaxMarkChange={handleMaxMarkChange}
                    />

                    {loadingStudents ? (
                      <div style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <i className="fa fa-spinner fa-spin" style={{ fontSize: 14 }}></i>
                        Loading students...
                      </div>
                    ) : fields.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                        No fields available.
                      </div>
                    ) : (
                      <MarksTable
                        students={students}
                        fields={fields.filter((f) => selectedFieldIds.includes(f.field_id))}
                        marksData={marksData}
                        onMarksChange={handleMarksChange}
                        onSaveStudent={handleSaveStudent}
                        savingMap={savingMap}
                        showObtainedMark={showObtainedMark}
                        showObtainedGrade={showObtainedGrade}
                      />
                    )}
                  </div>
                </div>
              )}

              {selectedClass && (
                <ActionButtons
                  onSave={handleSaveAll}
                  onCancel={handleCancel}
                  saving={savingAll}
                />
              )}

              {!selectedClass && (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                  Please select a class to start entering marks
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddExamSocialValueField;


